import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteConversation } from '@/api/conversations';
import { queryKeys } from '@/lib/query-keys';

/**
 * Borrar una conversación desde el inbox (solo ADMIN, espejo de `requireRole('ADMIN')`).
 *
 * Para qué existe: la "memoria" del bot ES el historial guardado, así que mientras no se pudiera
 * limpiar una conversación desde el panel, probar el bot de nuevo (o quitar de la vista un chat que
 * se ensució con pruebas) obligaba a borrarla por base de datos — y el bot seguía respondiendo con
 * lo que había quedado escrito ahí.
 *
 * Invalidaciones, y por qué cada una:
 *  - **todo el módulo de conversaciones** (`all`): el chat desaparece de la bandeja y su detalle e
 *    historial dejan de existir; se barren de golpe todas las combinaciones de filtros y páginas;
 *  - **el CRM**: el lead es la misma fila de `conversations`, así que también sale del kanban;
 *  - **las órdenes**: si la conversación tenía ventas, quedan conservadas pero sin el chat que las
 *    originó, y los listados de pedidos muestran ese dato.
 */
export interface DeleteConversationInput {
  conversationId: string;
  /** Segunda confirmación: borrar aunque la conversación tenga órdenes registradas. */
  permitirConOrdenes?: boolean;
}

export function useDeleteConversation(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, permitirConOrdenes }: DeleteConversationInput) =>
      deleteConversation(tenantId ?? '', conversationId, { permitirConOrdenes }),
    onSuccess: () => {
      if (!tenantId) return;

      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.crm.all(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all(tenantId) });
    },
  });
}